import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SafeIcon from '../common/SafeIcon';

export default function DataProcessingAgreement() {
  const lastUpdated = "December 1, 2025";
  
  const tocItems = [
    { id: 'definitions', title: 'Key Definitions' },
    { id: 'details', title: 'Details of Data Processing' },
    { id: 'processor-obligations', title: "Processor's Obligations" },
    { id: 'controller-obligations', title: "Controller's Obligations and Instructions" },
    { id: 'security-measures', title: 'Our Robust Data Security Measures' },
    { id: 'breach-response', title: 'Data Breach Notification and Response' },
    { id: 'data-deletion', title: 'Deletion and Return of Data' }
  ];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle smooth scroll for TOC to avoid HashRouter conflicts and account for fixed navbar
  const handleScrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // height of fixed navbar (h-20 = 80px)
      const y = element.getBoundingClientRect().top + window.scrollY - navbarHeight - 32; // 32px extra padding
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dc-bg text-dc-text">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Legal Hero */}
          <div className="mb-16 text-center">
            <div className="inline-block bg-dc-teal-deep/5 text-dc-teal-signal text-xs font-michroma px-4 py-2 rounded-full mb-6 border border-dc-teal-signal/20">
              LEGAL
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Data Processing Agreement</h1>
            <p className="text-lg text-dc-text/70 max-w-3xl mx-auto mb-6 leading-relaxed">
              This Data Processing Agreement ('DPA') is entered into by and between RepUpgrade ('Processor') and the customer utilizing RepUpgrade's services ('Controller'). This DPA sets forth the obligations and responsibilities of both parties concerning the processing of personal data.
            </p>
            <div className="max-w-3xl mx-auto text-sm text-dc-text/60 space-y-4">
              <p>
                This Agreement is an integral part of the main service agreement between RepUpgrade and the Controller and is intended to ensure compliance with applicable data protection laws, including but not limited to the General Data Protection Regulation (GDPR) and other relevant privacy legislation.
              </p>
              <p>
                By utilizing RepUpgrade's services, the Controller acknowledges and agrees to the terms outlined herein, affirming a commitment to robust data privacy and security practices.
              </p>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-soft mb-16">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <SafeIcon name="List" className="w-5 h-5 text-dc-teal-signal" />
              Table of Contents
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {tocItems.map((item, index) => (
                <a 
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => handleScrollToSection(e, item.id)}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-dc-bg transition-colors group cursor-pointer"
                >
                  <span className="text-dc-teal-signal font-michroma text-sm mt-1 group-hover:text-dc-teal-deep">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-dc-text group-hover:text-dc-teal-signal font-medium">
                    {item.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* DPA Sections */}
          <div className="space-y-16">
            <section id="definitions">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                1. Key Definitions
              </h2>
              <div className="grid gap-4">
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-dc-text mb-2">Personal Data</h3>
                  <p className="text-sm text-dc-text/80 leading-relaxed">
                    Any information relating to an identified or identifiable natural person ('Data Subject'); an identifiable natural person is one who can be identified, directly or indirectly, in particular by reference to an identifier such as a name, an identification number, location data, an online identifier or to one or more factors specific to the physical, physiological, genetic, mental, economic, cultural or social identity of that natural person.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-dc-text mb-2">Data Subject</h3>
                  <p className="text-sm text-dc-text/80 leading-relaxed">
                    The identified or identifiable natural person to whom Personal Data relates.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-dc-text mb-2">Processing</h3>
                  <p className="text-sm text-dc-text/80 leading-relaxed">
                    Any operation or set of operations which is performed on Personal Data or on sets of Personal Data, whether or not by automated means, such as collection, recording, organisation, structuring, storage, adaptation or alteration, retrieval, consultation, use, disclosure by transmission, dissemination or otherwise making available, alignment or combination, restriction, erasure or destruction.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-dc-text mb-2">Controller</h3>
                    <p className="text-sm text-dc-text/80 leading-relaxed">
                      The natural or legal person, public authority, agency or other body which, alone or jointly with others, determines the purposes and means of the Processing of Personal Data.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-dc-text mb-2">Processor</h3>
                    <p className="text-sm text-dc-text/80 leading-relaxed">
                      A natural or legal person, public authority, agency or other body which Processes Personal Data on behalf of the Controller.
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-dc-text mb-2">Supervisory Authority</h3>
                    <p className="text-sm text-dc-text/80 leading-relaxed">
                      An independent public authority which is established by a Member State pursuant to Article 51 of the GDPR.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-dc-text mb-2">Data Breach</h3>
                    <p className="text-sm text-dc-text/80 leading-relaxed">
                      A breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, Personal Data transmitted, stored or otherwise Processed.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section id="details">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                2. Details of Data Processing
              </h2>
              <div className="space-y-8 text-dc-text/80 leading-relaxed">
                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg flex items-center gap-2">
                    <SafeIcon name="Target" className="w-5 h-5 text-dc-teal-signal" /> Nature and Scope of Processing
                  </h3>
                  <p>
                    RepUpgrade processes personal data as necessary to provide its reputation management and marketing services, which include monitoring online mentions, analyzing sentiment, and assisting clients in responding to and improving their online presence. The scope of processing is limited to data directly relevant to these services and client instructions.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h3 className="font-bold text-dc-text mb-4 text-lg">Categories of Personal Data Processed</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <SafeIcon name="Check" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                      <div><span className="font-bold text-dc-text">Identification and Contact Data:</span> Names, email addresses, phone numbers, postal addresses, social media handles.</div>
                    </li>
                    <li className="flex items-start gap-3">
                      <SafeIcon name="Check" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                      <div><span className="font-bold text-dc-text">Online Activity Data:</span> IP addresses, browser type, operating system, interaction data with RepUpgrade services or client websites, public social media posts, reviews, and comments related to the data subject.</div>
                    </li>
                    <li className="flex items-start gap-3">
                      <SafeIcon name="Check" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                      <div><span className="font-bold text-dc-text">Professional Data:</span> Job title, company name, industry, professional affiliations.</div>
                    </li>
                    <li className="flex items-start gap-3">
                      <SafeIcon name="Check" className="w-5 h-5 text-dc-teal-signal shrink-0 mt-0.5" />
                      <div><span className="font-bold text-dc-text">Any other data:</span> Any other personal data made publicly available online and relevant to the client's reputation management needs.</div>
                    </li>
                  </ul>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-dc-text mb-3 text-lg">Categories of Data Subjects</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <div><span className="font-bold text-dc-text">Clients/Customers:</span> Individuals who directly engage RepUpgrade for services.</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <div><span className="font-bold text-dc-text">Individuals publicly mentioned online:</span> Any individuals whose personal data is publicly available online and relevant to the reputation of RepUpgrade's clients.</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <div><span className="font-bold text-dc-text">Website visitors:</span> Users interacting with RepUpgrade's own website or client websites where RepUpgrade provides analytical services.</div>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-dc-text mb-3 text-lg">Purpose of Processing</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <div><span className="font-bold text-dc-text">Reputation Management:</span> To monitor, analyze, and report on online mentions, reviews, and public sentiment.</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <div><span className="font-bold text-dc-text">Service Delivery:</span> To provide agreed-upon reputation management and marketing services.</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <div><span className="font-bold text-dc-text">Communication:</span> To communicate with clients regarding their services and deliver reports.</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-dc-teal-signal mt-2 shrink-0" />
                        <div><span className="font-bold text-dc-text">Service Improvement:</span> To analyze usage data and feedback.</div>
                      </li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-dc-text mb-3 text-lg">Duration & Nature of Operations</h3>
                  <p className="mb-4">
                    Personal data will be processed for the duration necessary to fulfill the purposes for which it was collected, as outlined in the service agreement with the client, or until such time as explicitly instructed by the client to cease processing, subject to any legal retention requirements.
                  </p>
                  <p className="font-bold text-dc-text mb-2">Operations include:</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <SafeIcon name="DownloadCloud" className="w-4 h-4 text-dc-teal-signal" />
                      <span><strong>Collection and Retrieval:</strong> Gathering personal data from public sources and client-provided information.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <SafeIcon name="Database" className="w-4 h-4 text-dc-teal-signal" />
                      <span><strong>Storage and Organization:</strong> Storing data securely within RepUpgrade's systems and organizing it.</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <SafeIcon name="BarChart2" className="w-4 h-4 text-dc-teal-signal" />
                      <span><strong>Analysis and Reporting:</strong> Analyzing data for sentiment, trends, and impact, generating reports.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="processor-obligations">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                3. Processor's Obligations
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:border-dc-teal-signal/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center mb-4">
                    <SafeIcon name="Lock" className="w-5 h-5 text-dc-teal-signal" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Confidentiality</h3>
                  <p className="text-sm text-dc-text/80">RepUpgrade shall ensure that all persons authorized to process the personal data have committed themselves to confidentiality or are under an appropriate statutory obligation of confidentiality. This commitment extends to all data accessed or processed during the provision of services.</p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:border-dc-teal-signal/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center mb-4">
                    <SafeIcon name="Shield" className="w-5 h-5 text-dc-teal-signal" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Security Measures</h3>
                  <p className="text-sm text-dc-text/80">RepUpgrade shall implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including measures to prevent unauthorized access, disclosure, alteration, or destruction of personal data.</p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:border-dc-teal-signal/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center mb-4">
                    <SafeIcon name="Users" className="w-5 h-5 text-dc-teal-signal" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Handling of Sub-processors</h3>
                  <p className="text-sm text-dc-text/80">RepUpgrade shall not engage another processor without prior specific or general written authorization of the Controller. Where a sub-processor is engaged, RepUpgrade shall ensure the same data protection obligations as set out in this DPA are imposed on that sub-processor.</p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:border-dc-teal-signal/30 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-dc-teal-signal/10 flex items-center justify-center mb-4">
                    <SafeIcon name="LifeBuoy" className="w-5 h-5 text-dc-teal-signal" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Assistance to the Controller</h3>
                  <p className="text-sm text-dc-text/80">RepUpgrade shall assist the Controller by appropriate technical and organizational measures, insofar as this is possible, for the fulfilment of the Controller's obligation to respond to requests for exercising data subjects' rights.</p>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:border-dc-teal-signal/30 transition-colors sm:col-span-2">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-4">
                    <SafeIcon name="AlertTriangle" className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Data Breach Notification</h3>
                  <p className="text-sm text-dc-text/80">RepUpgrade shall notify the Controller without undue delay after becoming aware of a personal data breach, providing the Controller with sufficient information to meet any obligations to report or inform data subjects of the breach.</p>
                </div>
              </div>
            </section>

            <section id="controller-obligations">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                4. Controller's Obligations and Instructions
              </h2>
              <div className="bg-dc-teal-deep/5 border border-dc-teal-signal/20 rounded-2xl p-8">
                <div className="space-y-6 text-dc-text/80 leading-relaxed">
                  <p className="flex items-start gap-3">
                    <SafeIcon name="CheckCircle" className="w-6 h-6 text-dc-teal-signal shrink-0" />
                    <span>As the Controller, you are solely responsible for establishing a lawful basis for the processing of all personal data submitted to RepUpgrade. This ensures that all data handling complies with applicable data protection laws and regulations.</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <SafeIcon name="FileText" className="w-6 h-6 text-dc-teal-signal shrink-0" />
                    <span>You must provide RepUpgrade with valid, documented instructions for processing the personal data. These instructions dictate the scope and purpose of our data processing activities, and RepUpgrade will not process data outside of these specified directives.</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <SafeIcon name="Shield" className="w-6 h-6 text-dc-teal-signal shrink-0" />
                    <span>The Controller is responsible for ensuring the accuracy, integrity, and legality of all personal data provided to RepUpgrade. This includes verifying that the data is up-to-date and collected in accordance with all relevant privacy laws before being shared.</span>
                  </p>
                </div>
              </div>
            </section>

            <section id="security-measures">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                5. Our Robust Data Security Measures
              </h2>
              <p className="text-lg text-dc-text/70 mb-8 leading-relaxed">
                At RepUpgrade, the security and confidentiality of your personal data are paramount. We implement a comprehensive suite of technical and organizational measures designed to protect your information from unauthorized access, disclosure, alteration, and destruction.
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                    <SafeIcon name="Lock" className="w-6 h-6 text-blue-500" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Data Encryption</h3>
                  <p className="text-sm text-dc-text/70">All personal data is encrypted both in transit and at rest using industry-standard protocols to protect against unauthorized access and breaches.</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mb-4">
                    <SafeIcon name="Key" className="w-6 h-6 text-purple-500" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Strict Access Controls</h3>
                  <p className="text-sm text-dc-text/70">Access to systems and data is granted on a 'need-to-know' basis, enforced with multi-factor authentication and regularly reviewed access policies.</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
                    <SafeIcon name="EyeOff" className="w-6 h-6 text-green-500" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Pseudonymisation</h3>
                  <p className="text-sm text-dc-text/70">Where feasible, personal data is pseudonymised or anonymised to minimize direct identifiability, enhancing data privacy without hindering processing.</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center mb-4">
                    <SafeIcon name="Activity" className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Continuous Audits</h3>
                  <p className="text-sm text-dc-text/70">Our systems undergo regular internal and external security audits, penetration testing, and vulnerability assessments to identify risks proactively.</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                    <SafeIcon name="Database" className="w-6 h-6 text-indigo-500" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Robust Backup</h3>
                  <p className="text-sm text-dc-text/70">Comprehensive data backup and recovery procedures are in place, ensuring data availability and integrity in the event of unforeseen incidents.</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mb-4">
                    <SafeIcon name="Users" className="w-6 h-6 text-teal-500" />
                  </div>
                  <h3 className="font-bold text-dc-text mb-2">Employee Training</h3>
                  <p className="text-sm text-dc-text/70">All RepUpgrade employees receive mandatory and ongoing training on data protection, security best practices, and our privacy policies.</p>
                </div>
              </div>
            </section>

            <section id="breach-response">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                6. Data Breach Notification and Response
              </h2>
              <div className="space-y-6">
                <p className="text-dc-text/80 leading-relaxed">
                  RepUpgrade is committed to the highest standards of data security. In the unlikely event of a personal data breach, we adhere to a strict protocol to ensure timely notification and effective mitigation in accordance with applicable data protection laws.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-6 relative">
                  {/* Decorative connecting line for desktop */}
                  <div className="hidden sm:block absolute top-1/2 left-0 w-full h-px bg-gray-200 -z-10" />
                  
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative">
                    <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-dc-teal-deep text-white font-michroma text-sm flex items-center justify-center border-4 border-dc-bg">1</div>
                    <h3 className="font-bold text-dc-text mb-2 mt-2">Prompt Detection</h3>
                    <p className="text-sm text-dc-text/70">RepUpgrade maintains robust security measures and monitoring systems designed to detect personal data breaches without undue delay. Our team is trained to identify and respond swiftly.</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative">
                    <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-dc-teal-deep text-white font-michroma text-sm flex items-center justify-center border-4 border-dc-bg">2</div>
                    <h3 className="font-bold text-dc-text mb-2 mt-2">Immediate Notification</h3>
                    <p className="text-sm text-dc-text/70">Upon becoming aware of a breach, RepUpgrade will notify the Controller without undue delay, and in any event within 48 hours of discovery, through agreed communication channels.</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative">
                    <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-dc-teal-deep text-white font-michroma text-sm flex items-center justify-center border-4 border-dc-bg">3</div>
                    <h3 className="font-bold text-dc-text mb-2 mt-2">Comprehensive Information</h3>
                    <p className="text-sm text-dc-text/70">Our notification will include the nature of the breach, categories of data subjects concerned, likely consequences, and measures taken or proposed to address the breach.</p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative">
                    <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-dc-teal-deep text-white font-michroma text-sm flex items-center justify-center border-4 border-dc-bg">4</div>
                    <h3 className="font-bold text-dc-text mb-2 mt-2">Mitigation & Cooperation</h3>
                    <p className="text-sm text-dc-text/70">RepUpgrade will take all reasonable steps to mitigate effects and prevent further unauthorized access. We will fully cooperate with the Controller in investigating the breach.</p>
                  </div>
                </div>
                
                <p className="text-sm text-dc-text/50 italic mt-4">
                  * This protocol is subject to the specific terms outlined in the Data Processing Agreement between RepUpgrade and the Controller. Communication channels and detailed procedures will be established therein.
                </p>
              </div>
            </section>

            <section id="data-deletion">
              <h2 className="text-2xl font-bold mb-6 text-dc-teal-deep border-l-4 border-dc-teal-signal pl-4 py-1">
                7. Deletion and Return of Data
              </h2>
              <div className="bg-dc-bg p-8 rounded-2xl border border-gray-100 flex gap-6 items-start">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                  <SafeIcon name="Trash2" className="w-6 h-6 text-dc-teal-signal" />
                </div>
                <p className="text-dc-text/80 leading-relaxed">
                  Upon termination of services or expiration of the Data Processing Agreement, RepUpgrade ensures that all personal data processed on behalf of the Controller is securely deleted or returned, as per the Controller's instructions. This process will be completed within a maximum of 90 days from the date of termination or expiration, unless legal obligations require retention for a longer period. We will provide confirmation of data deletion or return.
                </p>
              </div>
            </section>
          </div>

          {/* Back to Home CTA */}
          <div className="mt-20 pt-10 border-t border-gray-200">
            <div className="bg-dc-bg rounded-3xl p-10 text-center">
              <h2 className="text-2xl font-bold text-dc-text mb-4">Return to RepUpgrade</h2>
              <p className="text-dc-text/70 mb-8 max-w-2xl mx-auto">
                Back to building trust, content, and clear next steps for your local business.
              </p>
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-dc-teal-signal/30 text-dc-text font-semibold py-3 px-8 rounded-xl shadow-sm transition-all"
              >
                <SafeIcon name="ArrowLeft" className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}